import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

interface HeatMapCalendarProps {
  completionDates: string[]; // Array of YYYY-MM-DD dates
  startDate?: string; // Optional start date in YYYY-MM-DD format (creation date)
  targetDuration?: number; // Optional target duration in days
  frequency?: 'daily' | 'weekly' | 'custom'; // Habit frequency
  targetDaysPerWeek?: number; // For weekly frequency
  allowedWeekdays?: number[]; // For custom frequency (0=Sun, 1=Mon, etc.)
}

const HeatMapCalendar: React.FC<HeatMapCalendarProps> = ({
  completionDates,
  startDate,
  targetDuration,
  frequency = 'daily',
  targetDaysPerWeek,
  allowedWeekdays
}) => {
  const { t } = useLanguage();
  // Generate 16 weeks of dates starting from creation date or fallback to current logic
  const generateWeeks = (): Date[][] => {
    const weeks: Date[][] = [];

    // Use startDate if provided, otherwise fallback to original logic
    let actualStartDate: Date;
    if (startDate) {
      actualStartDate = new Date(startDate);
      actualStartDate.setHours(0, 0, 0, 0);

      // Adjust to the Monday before or on start date
      const dayOfWeek = actualStartDate.getDay();
      // getDay() returns 0=Sunday, 1=Monday, ..., 6=Saturday
      // Calculate days to go back to Monday: if Sunday (0), go back 6 days; otherwise go back to Monday
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      if (daysToMonday > 0) {
        actualStartDate.setDate(actualStartDate.getDate() - daysToMonday);
      }
    } else {
      // Fallback to original logic: last 12 weeks ending today
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Find the Saturday of the current week (end of week)
      const endDate = new Date(today);
      const daysUntilSaturday = 6 - today.getDay();
      endDate.setDate(today.getDate() + daysUntilSaturday);

      // Go back 12 weeks from end date to find start
      actualStartDate = new Date(endDate);
      actualStartDate.setDate(endDate.getDate() - (12 * 7 - 1));

      // Adjust to the Monday before start date
      const dayOfWeek = actualStartDate.getDay();
      // getDay() returns 0=Sunday, 1=Monday, ..., 6=Saturday
      // Calculate days to go back to Monday: if Sunday (0), go back 6 days; otherwise go back to Monday
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      if (daysToMonday > 0) {
        actualStartDate.setDate(actualStartDate.getDate() - daysToMonday);
      }
    }

    // Generate 16 weeks (about 4 months) from start date
    let currentWeek: Date[] = [];
    let currentDate = new Date(actualStartDate);

    // Generate enough days to cover 16 full weeks
    for (let i = 0; i < 16 * 7; i++) {
      const date = new Date(currentDate);
      currentWeek.push(date);

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }

    return weeks;
  };

  const dateToString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const weeks = generateWeeks();

  const isCompleted = (date: Date): boolean => {
    const dateStr = dateToString(date);
    return completionDates.includes(dateStr);
  };

  const isFutureDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate > today;
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return dateToString(date) === dateToString(today);
  };

  const isBeforeCreation = (date: Date): boolean => {
    if (!startDate) return false;
    const creationDate = new Date(startDate);
    creationDate.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < creationDate;
  };

  const isWithinTargetDuration = (date: Date): boolean => {
    if (!startDate || !targetDuration) return false;
    const creationDate = new Date(startDate);
    creationDate.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    // Check if date is within the target duration period
    const endDate = new Date(creationDate);
    endDate.setDate(creationDate.getDate() + targetDuration - 1);

    if (checkDate < creationDate || checkDate > endDate) {
      return false;
    }

    // Now check frequency-based commitment
    const dayOfWeek = checkDate.getDay(); // 0=Sunday, 1=Monday, etc.

    switch (frequency) {
      case 'daily':
        return true; // All days within duration

      case 'weekly':
        // For weekly, we need to determine which days of the week they should do it
        // Since we don't have specific weekday info for weekly, we'll assume they do it regularly
        // This could be enhanced later if needed
        return true; // For now, show on all days within duration

      case 'custom':
        // Only show on allowed weekdays
        return allowedWeekdays ? allowedWeekdays.includes(dayOfWeek) : false;

      default:
        return true;
    }
  };

  const isMissed = (date: Date): boolean => {
    return !isFutureDate(date) && !isCompleted(date) && !isToday(date) && !isBeforeCreation(date);
  };

  // Get intensity class based on completion
  const getIntensityClass = (date: Date): string => {
    if (isFutureDate(date)) {
      // Future dates within target duration get a subtle commitment indicator
      if (isWithinTargetDuration(date)) {
        return 'bg-gray-900/50 border-purple-600/30 ring-1 ring-purple-600/20'; // Future committed days
      }
      return 'bg-gray-900/50 border-gray-800'; // Future dates
    }

    // Dates before skill creation - treat as neutral
    if (isBeforeCreation(date)) {
      return 'bg-gray-900/30 border-gray-800/50'; // Very dim for dates before creation
    }

    if (isCompleted(date)) {
      // Extra glow for today's completion
      if (isToday(date)) {
        return 'bg-gradient-to-br from-green-500 to-emerald-400 border-green-400 shadow-[0_0_12px_rgba(34,197,94,0.8)] ring-2 ring-green-400/50'; // Today & completed
      }
      return 'bg-gradient-to-br from-blue-500 to-cyan-400 border-blue-400 shadow-[0_0_8px_rgba(59,130,246,0.5)]'; // Completed
    }

    // Special styling for today if not completed
    if (isToday(date)) {
      return 'bg-gray-700/70 border-orange-500 ring-1 ring-orange-500/50'; // Today but not completed
    }

    if (isMissed(date)) {
      return 'bg-red-900/50 border-red-500 ring-1 ring-red-500/30'; // Missed days
    }

    return 'bg-gray-800/50 border-gray-700'; // Not completed
  };

  const monthLabels = [
    t.calendar.monthsShort.jan,
    t.calendar.monthsShort.feb,
    t.calendar.monthsShort.mar,
    t.calendar.monthsShort.apr,
    t.calendar.monthsShort.may,
    t.calendar.monthsShort.jun,
    t.calendar.monthsShort.jul,
    t.calendar.monthsShort.aug,
    t.calendar.monthsShort.sep,
    t.calendar.monthsShort.oct,
    t.calendar.monthsShort.nov,
    t.calendar.monthsShort.dec,
  ];
  const dayLabels = [
    t.calendar.weekDaysShort.mon,
    t.calendar.weekDaysShort.tue,
    t.calendar.weekDaysShort.wed,
    t.calendar.weekDaysShort.thu,
    t.calendar.weekDaysShort.fri,
    t.calendar.weekDaysShort.sat,
    t.calendar.weekDaysShort.sun,
  ];

  // Calculate which months are shown and where
  const getMonthHeaders = (): { month: string; columnIndex: number }[] => {
    const headers: { month: string; columnIndex: number }[] = [];
    let lastMonth = -1;

    weeks.forEach((week, weekIndex) => {
      const firstDay = week[0];
      const currentMonth = firstDay.getMonth();
      
      // Show month label when month changes or on first week
      if (currentMonth !== lastMonth) {
        headers.push({
          month: monthLabels[currentMonth],
          columnIndex: weekIndex
        });
        lastMonth = currentMonth;
      }
    });

    return headers;
  };

  const monthHeaders = getMonthHeaders();

  return (
    <div className="heat-map-calendar">
      {/* Month Labels */}
      <div className="relative mb-2 px-3 h-4">
        {monthHeaders.map((header, index) => (
          <span
            key={index}
            className="absolute text-xs font-rajdhani text-gray-400 font-semibold"
            style={{ left: `${(header.columnIndex * 100) / weeks.length}%` }}
          >
            {header.month}
          </span>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="flex gap-1">
        {/* Day Labels */}
        <div className="flex flex-col gap-1 mr-1">
          {dayLabels.map((day, index) => (
            <div
              key={index}
              className="w-4 h-3 flex items-center justify-center text-[10px] font-rajdhani text-gray-500"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Weeks Grid */}
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="flex flex-col gap-1">
            {week.map((date, dayIndex) => {
              const dateStr = dateToString(date);
              const completed = isCompleted(date);
              const future = isFutureDate(date);
              const missed = isMissed(date);
              const beforeCreation = isBeforeCreation(date);

              return (
                <div
                  key={dayIndex}
                  className={`w-3 h-3 rounded-sm border transition-all ${getIntensityClass(date)} group relative flex items-center justify-center`}
                  title={`${dateStr}${completed ? ' ✓' : missed ? ' ✗' : beforeCreation ? ' (before creation)' : future ? ' (future)' : ''}`}
                >
                  {/* Red X for missed days */}
                  {missed && (
                    <span className="text-red-400 text-[8px] font-bold leading-none">✗</span>
                  )}

                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black border border-gray-700 rounded text-xs font-rajdhani text-white whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 pointer-events-none">
                    {dateStr}
                    {completed && <span className="text-green-400 ml-1">✓</span>}
                    {missed && <span className="text-red-400 ml-1">✗</span>}
                    {beforeCreation && <span className="text-gray-600 ml-1">(before creation)</span>}
                    {future && <span className="text-gray-500 ml-1">(future)</span>}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end gap-3 mt-3 text-xs font-rajdhani text-gray-500">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-gray-800/50 border border-gray-700"></div>
          <span>None</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-gradient-to-br from-blue-500 to-cyan-400 border border-blue-400"></div>
          <span>Done</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-red-900/50 border border-red-500 flex items-center justify-center">
            <span className="text-red-400 text-[6px] font-bold leading-none">✗</span>
          </div>
          <span>Missed</span>
        </div>
      </div>
    </div>
  );
};

export default HeatMapCalendar;
